import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router, ActivatedRoute, Params} from '@angular/router';
import {InputButtonIconType} from '../../core/button/button.utils';
import {TableSortExtension} from '../../core/table/sort/TableSortExtension';
import {TablePaginationUtils} from '../../core/table/pagination/TablePaginationUtils';
import {ArrayUtils} from '../../../utils/ArrayUtils';
import {AuthUtils} from '../../../utils/AuthUtils';
import {PermissionTargetEnum} from '../../../enums/PermissionTargetEnum';
import {MessageService} from '../../../services/message.service';
import {ConfirmService} from '../../../services/confirm.service';
import {SelectItem} from '../../../dtos/SelectItem';
import {FirmwareService} from '../../../services/rest/firmware.service';
import {ModuleTypeService, ModuleType} from '../../../services/rest/module-type.service';
import {ChipTypeService} from '../../../services/rest/chip-type.service';
import {DisplayTypeService} from '../../../services/rest/display-type.service';

export interface FirmwareDto {
  id: number;
  softwareType: string;
  moduleTypeCode: string;
  moduleTypeName: string;
  chipTypeCode: string;
  chipTypeName: string;
  displayTypeCode: string;
  displayTypeName: string;
  version: number;
  fileId: number;
  fileName: string;
  downloadUrl: string;
}

export interface FirmwareFilter {
  page: number;
  size: number;
  softwareType?: string;
  moduleTypeCode?: string;
  chipTypeCode?: string;
  displayTypeCode?: string;
  version?: number;
  sortList?: {name: string, ascending: boolean}[];
}

@Component({
  selector: 'app-firmware-table',
  templateUrl: './firmware-table.component.html',
  styleUrls: ['./firmware-table.component.scss', '../../core/table.scss']
})
export class FirmwareTableComponent extends TableSortExtension implements OnInit, AfterViewInit {
  InputButtonIconType = InputButtonIconType;
  ArrayUtils = ArrayUtils;

  firmwares: FirmwareDto[] = [];
  selectedSoftwareType: string = null;
  selectedModuleTypeCode: string = null;
  selectedChipTypeCode: string = null;
  selectedDisplayTypeCode: string = null;
  selectedVersion: number = null;

  loading = true;
  first = 0;
  paginationSize: number = null;
  total: number;
  lastFilter: FirmwareFilter = this.createFilter();

  @ViewChild('tableParentDiv', {read: ElementRef, static: false}) tableParentDiv: ElementRef;
  userCanEdit: boolean = AuthUtils.canEdit(PermissionTargetEnum.FIRMWARE);

  softwareTypeOptions: SelectItem[] = [
    {id: 'INTERNAL', name: 'INTERNAL', description: ''},
    {id: 'INTEGRATION', name: 'INTEGRATION', description: ''},
    {id: 'PRODUCTION', name: 'PRODUCTION', description: ''},
    {id: 'BOOTLOADER', name: 'BOOTLOADER', description: ''},
    {id: 'PARTITIONS', name: 'PARTITIONS', description: ''},
    {id: 'OTA_DATA', name: 'OTA_DATA', description: ''},
  ];

  moduleTypeOptions: SelectItem[] = [];
  chipTypeOptions: SelectItem[] = [];
  displayTypeOptions: SelectItem[] = [];

  constructor(private router: Router,
              private route: ActivatedRoute,
              private service: FirmwareService,
              private moduleTypeService: ModuleTypeService,
              private chipTypeService: ChipTypeService,
              private displayTypeService: DisplayTypeService,
              private confirmService: ConfirmService,
              private messageService: MessageService) {
    super();
    this.setOnSortOrderChangedCallbackFunction(this.onSearch);
  }

  ngOnInit(): void {
    this.moduleTypeService.getAll().subscribe(options => {
      this.moduleTypeOptions = options.map(m => ({
        id: m.code as any,
        name: m.name,
        description: m.code
      }));
    });
    this.chipTypeService.getAll().subscribe(options => {
      this.chipTypeOptions = options.map(c => ({
        id: c.code as any,
        name: c.name,
        description: c.code
      }));
    });
    this.displayTypeService.getAll().subscribe(options => {
      this.displayTypeOptions = options.map(d => ({
        id: d.code as any,
        name: d.name,
        description: d.code
      }));
    });
    this.route.queryParams.subscribe(params => this.processParams(params));
  }

  ngAfterViewInit(): void {
    TablePaginationUtils.calculateNumberOfRowsForTableBasedOnParentDivSizeAndFetchUsers(this.tableParentDiv, this.onSearch.bind(this));
  }

  onCreate(): void {
    this.router.navigate(['/firmware/create']);
  }

  onEdit(item: FirmwareDto): void {
    this.router.navigate(['/firmware/' + item.id]);
  }

  onDelete(item: FirmwareDto): void {
    this.confirmService.confirm(
      `Firmware löschen`,
      `Wollen Sie die Firmware ${item.softwareType} (${item.moduleTypeName}) v${item.version} wirklich löschen? <br>Dieser Vorgang kann nicht rückgängig gemacht werden.`,
      () => {
        this.service.delete(item.id).toPromise().then(() => {
          this.messageService.addSuccessMessage(`Firmware ${item.softwareType} (${item.moduleTypeName}) v${item.version} wurde erfolgreich gelöscht!`);
          this.onSearch();
        });
      });
  }

  searchLazy(event: any) {
    if (!this.paginationSize) {
      return;
    }
    let firstRow = 0;
    if (event.first) {
      firstRow = event.first;
    }
    this.lastFilter.page = firstRow / this.paginationSize;
    this.lastFilter.size = this.paginationSize;
    this.search(this.lastFilter);
  }

  onSearch(paginationSize: number = this.paginationSize): void {
    this.paginationSize = paginationSize;
    if (paginationSize && paginationSize < this.firmwares.length) {
      this.firmwares = this.firmwares.slice(0, paginationSize);
    }
    this.lastFilter = this.createFilter();
    this.first = 0;
    this.search(this.lastFilter);
  }

  private search(filter: FirmwareFilter): void {
    this.loading = true;
    this.service.getAll(filter).subscribe((paged: any) => {
      this.firmwares = paged.values;
      this.total = paged.totalElements;
      this.loading = false;
    });
  }

  private createFilter(): FirmwareFilter {
    return {
      page: 0,
      size: this.paginationSize,
      softwareType: this.selectedSoftwareType,
      moduleTypeCode: this.selectedModuleTypeCode,
      chipTypeCode: this.selectedChipTypeCode,
      displayTypeCode: this.selectedDisplayTypeCode,
      version: this.selectedVersion,
      sortList: [{name: 'id', ascending: false}]
    };
  }

  private processParams(params: Params): void {
    let searchAfterwards = false;
    if (params.softwareType) {
      searchAfterwards = true;
      this.selectedSoftwareType = params.softwareType;
    }
    if (params.moduleTypeCode) {
      searchAfterwards = true;
      this.selectedModuleTypeCode = params.moduleTypeCode;
    }
    if (params.chipTypeCode) {
      searchAfterwards = true;
      this.selectedChipTypeCode = params.chipTypeCode;
    }
    if (params.displayTypeCode) {
      searchAfterwards = true;
      this.selectedDisplayTypeCode = params.displayTypeCode;
    }
    if (params.version) {
      searchAfterwards = true;
      this.selectedVersion = +params.version;
    }
    if (searchAfterwards) {
      this.onSearch();
    }
  }
}
